import type { ExtractedRouteMethod, NitroFetchOptions, NitroFetchRequest, TypedInternalResponse } from 'nitropack';

export async function useHubFetch<
  DefaultT = unknown,
  DefaultR extends NitroFetchRequest = NitroFetchRequest,
  T = DefaultT,
  R extends NitroFetchRequest = DefaultR,
  O extends NitroFetchOptions<R> = NitroFetchOptions<R>,
>(request: R, opts?: O): Promise<TypedInternalResponse<R, T, ExtractedRouteMethod<R, O>>> {
  const { accessTokenState } = useAuthState();
  const { clearSession, isTokenExpired } = useAuth();
  const { getApiUrl } = useServerSettings();
  const { notify } = useNotification();
  const { getDataFromIndexedDB, saveDataToIndexedDB } = useDb();

  const queueRequest = (requestData: any) => {
    const queue = JSON.parse(localStorage.getItem('apiQueue') || '[]');
    queue.push(requestData);
    localStorage.setItem('apiQueue', JSON.stringify(queue));
  };

  const processQueue = async () => {
    const queue = JSON.parse(localStorage.getItem('apiQueue') || '[]');
    for (const req of queue) {
      try {
        await $fetch(req.request, req.options);
      } catch (error) {
        console.error('Request failed during retry:', error);
      }
    }
    localStorage.setItem('apiQueue', '[]');
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('online', processQueue);
  }

  try {
    // ts-expect-error - because of nice types from ofetch <3
    const response = await $fetch<T>(request, {
      ...opts,
      baseURL: getApiUrl(),
      async onRequest(data: any) {
        if (isTokenExpired(accessTokenState.value)) {
          const result = await $fetch<ApiLoginReturn>('/refreshtoken', { baseURL: getApiUrl(), method: 'GET' });
          if (result.statusCode === 200) {
            accessTokenState.value = result.resultObject.apiKey;
          }
        }

        if (accessTokenState.value) {
          data.options.headers = {
            ...data.options.headers,
            Authorization: `Bearer ${accessTokenState.value}`,
          };
        }
      },
      onResponse: async (response) => {
        console.debug(response);
      },
      onResponseError: async (ctx) => {
        if (isTokenExpired(accessTokenState.value)) {
          const result = await $fetch<ApiLoginReturn>('/refreshtoken', { baseURL: getApiUrl(), method: 'GET' });
          if (result.statusCode === 200) {
            accessTokenState.value = result.resultObject.apiKey;
          }
        }

        if (ctx.response.status === 401) {
          clearSession();
          navigateTo('/auth');
          return;
        }

        let data: any = {};
        try {
          data = JSON.parse(ctx.response._data);
        } catch (e) {}

        notify({
          text: data?.responseMessage ?? '',
          title: `${ctx.response.status} - Ein Fehler ist aufgetreten`,
          type: 'error',
        });
      },

      retry: 3,
      retryDelay: 500,
      retryStatusCodes: [401],
    });

    await saveDataToIndexedDB({ data: response, id: request as string });
    if (typeof response === 'string') {
      return JSON.parse(response);
    } else {
      return response as TypedInternalResponse<R, T, ExtractedRouteMethod<R, O>>;
    }
  } catch (error) {
    if (!navigator.onLine) {
      queueRequest({ options: opts, request });

      const cachedData = await getDataFromIndexedDB(request as string);
      return cachedData?.data;
    } else {
      notify({
        text: 'Ein Fehler ist aufgetreten, bitte versuchen Sie es später erneut.',
        title: 'Fehler',
        type: 'error',
      });
      clearSession();
      navigateTo('/auth');
    }
    throw error;
  }
}
